import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Zap, Settings, ChevronRight, PanelLeftClose, Users } from 'lucide-react';
import { cn } from '../../lib/utils';
import { PAGES } from '../../pages/registry';
import { Separator } from '../ui/separator';
import ContextStore from '../../hooks/ContextStore';
import { dispatch } from '../../dispatch/dispatch';
import { actions } from '../../dispatch';
import { urls } from '../../dispatch/urls';

const WEEKLY_SEEN_KEY = 'weeklyReportSeen';   // same key as pages/WeeklyReport.page.tsx

interface NavItem {
  label:    string;
  icon:     React.ElementType;
  path:     string;
  permKey:  string;     // permission key to check
  badge?:   string;
}

// The page list lives in src/pages/registry.ts — add a page there, it appears here and in User Management
const NAV_ITEMS: NavItem[] = PAGES.map((p) => ({ label: p.label, icon: p.icon, path: p.path, permKey: p.key, badge: p.badge }));

interface SidebarProps {
  open:      boolean;
  collapsed: boolean;
  onClose:   () => void;
  onToggle:  () => void;
}

export function Sidebar({ open, collapsed, onClose, onToggle }: SidebarProps) {
  const { contextStore } = useContext(ContextStore);
  const location = useLocation();
  const navigate = useNavigate();

  const user = contextStore?.user;
  const initials = user?.email ? user.email.slice(0, 2).toUpperCase() : 'JD';
  const isAdmin = user?.type === 'admin';

  // Permission check
  const can = (key: string): boolean => {
    if (isAdmin) return true;
    if (!user?.permissions?.length) return false;
    if (user.permissions.includes('all')) return true;
    return user.permissions.includes(key);
  };

  const visibleItems = NAV_ITEMS.filter(item => can(item.permKey));

  // "NEW" on Weekly Report until the newest report has been opened (remembered per browser)
  const canWeekly = can('decisions');
  const [latestWeek, setLatestWeek] = useState<string | null>(null);
  const [seenWeek, setSeenWeek] = useState<string | null>(() => { try { return localStorage.getItem(WEEKLY_SEEN_KEY); } catch { return null; } });
  useEffect(() => {
    if (!canWeekly || !contextStore?.token) return;
    let alive = true;
    dispatch(actions.weeklyReports, {}, contextStore.token).then((r: any) => { if (alive && r?.reports?.[0]) setLatestWeek(r.reports[0].week); }).catch(() => {});
    const onSeen = () => { try { setSeenWeek(localStorage.getItem(WEEKLY_SEEN_KEY)); } catch { /* private mode */ } };
    window.addEventListener('weekly-report-seen', onSeen);
    return () => { alive = false; window.removeEventListener('weekly-report-seen', onSeen); };
  }, [canWeekly, contextStore?.token]);
  const weeklyNew = !!latestWeek && latestWeek !== seenWeek;

  // ⛔ Telegram Scout alerts: a supplier marked a product sold out (owner 2026-11-03)
  const [tgAlerts, setTgAlerts] = useState(0);
  useEffect(() => {
    if (!isAdmin || !contextStore?.token) return;
    let alive = true;
    const pull = () => fetch(`${urls.baseUrlRestApi}/telegram-scout?count=1`, { headers: { 'x-auth-token': contextStore.token } })
      .then((r) => r.json()).then((r) => { if (alive && typeof r?.alerts === 'number') setTgAlerts(r.alerts); }).catch(() => {});
    pull(); const t = setInterval(pull, 10 * 60 * 1000);
    window.addEventListener('tg-alerts-changed', pull);
    return () => { alive = false; clearInterval(t); window.removeEventListener('tg-alerts-changed', pull); };
  }, [isAdmin, contextStore?.token]);

  // 🩺 broken system checks (health watch, owner 2026-10-30: in the app, not Telegram)
  const [healthBad, setHealthBad] = useState(0);
  useEffect(() => {
    if (!isAdmin || !contextStore?.token) return;
    let alive = true;
    const pull = () => fetch(`${urls.baseUrlRestApi}/health-watch?count=1`, { headers: { 'x-auth-token': contextStore.token } })
      .then((r) => r.json()).then((r) => { if (alive && typeof r?.problems === 'number') setHealthBad(r.problems); }).catch(() => {});
    pull(); const t = setInterval(pull, 5 * 60 * 1000);
    window.addEventListener('health-changed', pull);
    return () => { alive = false; clearInterval(t); window.removeEventListener('health-changed', pull); };
  }, [isAdmin, contextStore?.token]);

  // ⏸ paused products that received new stock — resume them? (user 2026-10-27)
  const [pauseOpen, setPauseOpen] = useState(0);
  useEffect(() => {
    if (!isAdmin || !contextStore?.token) return;
    let alive = true;
    const pull = () => fetch(`${urls.baseUrlRestApi}/inventory/pause-reminders?count=1`, { headers: { 'x-auth-token': contextStore.token } })
      .then((r) => r.json()).then((r) => { if (alive && typeof r?.open === 'number') setPauseOpen(r.open); }).catch(() => {});
    pull(); const t = setInterval(pull, 5 * 60 * 1000);
    window.addEventListener('pause-reminders-changed', pull);
    return () => { alive = false; clearInterval(t); window.removeEventListener('pause-reminders-changed', pull); };
  }, [isAdmin, contextStore?.token]);

  // 🔔 higher supplier prices waiting for the admin's approval (shopping list, user 2026-10-26)
  const [buyPending, setBuyPending] = useState(0);
  useEffect(() => {
    if (!isAdmin || !contextStore?.token) return;
    let alive = true;
    const pull = () => fetch(`${urls.baseUrlRestApi}/inventory/shopping-list/requests?count=1`, { headers: { 'x-auth-token': contextStore.token } })
      .then((r) => r.json()).then((r) => { if (alive && typeof r?.pending === 'number') setBuyPending(r.pending); }).catch(() => {});
    pull(); const t = setInterval(pull, 5 * 60 * 1000);
    window.addEventListener('buy-requests-changed', pull);
    return () => { alive = false; clearInterval(t); window.removeEventListener('buy-requests-changed', pull); };
  }, [isAdmin, contextStore?.token]);

  const isActive = (path: string) => {
    if (path === '/dashboard/0') return location.pathname.startsWith('/dashboard');
    if (path.startsWith('/productgroup/')) return location.pathname === path;
    return location.pathname === path;
  };

  const handleNav = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-60 flex-col',
          'bg-sidebar border-r border-sidebar-border',
          'transition-transform duration-200 ease-in-out',
          open ? 'translate-x-0' : '-translate-x-full',
          !collapsed ? 'lg:translate-x-0' : 'lg:-translate-x-full',
        )}
      >
        {/* Brand */}
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand">
              <Zap className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-sm font-bold text-sidebar-foreground tracking-tight">JumiaDash</span>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden flex h-7 w-7 items-center justify-center rounded-md text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <Separator className="bg-sidebar-border" />

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          <p className="px-2 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-sidebar-muted select-none">
            Navigation
          </p>

          {visibleItems.map(({ label, icon: Icon, path, badge: staticBadge, permKey }) => {
            const active = isActive(path);
            const badge = permKey === 'decisions' && weeklyNew ? 'NEW' : permKey === 'shopping-list' && buyPending > 0 ? `🔔 ${buyPending}` : permKey === 'inventory-control' && pauseOpen > 0 ? `⏸ ${pauseOpen}` : permKey === 'system-health' && healthBad > 0 ? `❌ ${healthBad}` : permKey === 'telegram-scout' && tgAlerts > 0 ? `⛔ ${tgAlerts}` : staticBadge;
            return (
              <button
                key={path + label}
                onClick={() => handleNav(path)}
                className={cn(
                  'group flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition-all duration-150',
                  active
                    ? 'bg-sidebar-accent text-sidebar-foreground font-medium'
                    : 'text-sidebar-muted hover:bg-sidebar-accent/60 hover:text-sidebar-foreground'
                )}
              >
                <Icon className={cn('h-4 w-4 shrink-0 transition-colors', active ? 'text-brand' : 'text-sidebar-muted group-hover:text-sidebar-foreground')} />
                <span className="flex-1 truncate text-left">{label}</span>
                {badge && (
                  <span className="rounded-full bg-brand/20 px-1.5 py-0.5 text-[10px] font-semibold text-brand">
                    {badge}
                  </span>
                )}
                {active && <ChevronRight className="h-3 w-3 text-sidebar-muted shrink-0" />}
              </button>
            );
          })}

          <p className="px-2 pt-5 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-sidebar-muted select-none">
            System
          </p>

          {/* Admin: User Management */}
          {isAdmin && (
            <button
              onClick={() => handleNav('/admin/users')}
              className={cn(
                'group flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition-all duration-150',
                location.pathname === '/admin/users'
                  ? 'bg-sidebar-accent text-sidebar-foreground font-medium'
                  : 'text-sidebar-muted hover:bg-sidebar-accent/60 hover:text-sidebar-foreground'
              )}
            >
              <Users className={cn('h-4 w-4 shrink-0', location.pathname === '/admin/users' ? 'text-brand' : 'text-sidebar-muted group-hover:text-sidebar-foreground')} />
              <span className="flex-1 truncate text-left">User Management</span>
              {location.pathname === '/admin/users' && (
                <ChevronRight className="h-3 w-3 text-sidebar-muted shrink-0" />
              )}
            </button>
          )}

          <button
            onClick={() => handleNav('/settings')}
            className="group flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-sm text-sidebar-muted hover:bg-sidebar-accent/60 hover:text-sidebar-foreground transition-all duration-150"
          >
            <Settings className="h-4 w-4 shrink-0 text-sidebar-muted group-hover:text-sidebar-foreground transition-colors" />
            <span>Settings</span>
          </button>
        </nav>

        <Separator className="bg-sidebar-border" />

        {/* User footer */}
        <div className="p-3 flex items-center gap-2">
          <div className="flex flex-1 items-center gap-3 rounded-lg px-2 py-2 hover:bg-sidebar-accent/60 transition-colors cursor-default min-w-0">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand/20 text-[11px] font-bold text-brand">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-sidebar-foreground truncate">{user?.email || 'User'}</p>
              <p className="text-[10px] text-sidebar-muted">{isAdmin ? 'Admin' : 'User'}</p>
            </div>
          </div>

          <button
            onClick={onToggle}
            title="Collapse sidebar"
            className="hidden lg:flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        </div>
      </aside>
    </>
  );
}
